import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ContactInfo from './components/ContactInfo';
import ProjectInquiryForm from './components/ProjectInquiryForm';
import FAQSection from './components/FAQSection';
import ProjectBriefDownload from './components/ProjectBriefDownload';
import Icon from '../../components/AppIcon';

const Contact = () => {
  return (
    <>
      <Helmet>
        <title>Contact - Start Your Project | Mr Mike Technologies</title>
        <meta 
          name="description" 
          content="Ready to start your project? Contact Mr Mike Technologies for full-stack development services. Get in touch for web applications, technical consulting, and production-ready solutions." 
        />
        <meta name="keywords" content="contact developer, hire full-stack developer, project inquiry, web development consultation, technical consulting, Mr Mike Technologies" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 lg:pt-24">
          <section className="py-12 md:py-16 lg:py-20 px-4 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <Breadcrumbs />

              <div className="text-center mb-12 md:mb-16 lg:mb-20">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                  <div className="w-2 h-2 rounded-full bg-success animate-pulse-glow" />
                  <span className="text-sm md:text-base font-medium text-primary">
                    Available for New Projects
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold font-heading text-foreground mb-4 md:mb-6">
                  Let's Build Something
                  <span className="block text-primary mt-2">Amazing Together</span>
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                  Ready to transform your vision into a production-ready solution? I'm here to help you build scalable, high-performance applications that drive real business results.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16 md:mb-20 lg:mb-24">
                <ContactInfo />
                <ProjectInquiryForm />
              </div>

              <div className="mb-16 md:mb-20 lg:mb-24">
                <ProjectBriefDownload />
              </div>

              <div className="mb-16 md:mb-20 lg:mb-24">
                <FAQSection />
              </div>

              <div className="p-8 md:p-10 lg:p-12 rounded-lg bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
                <div className="max-w-3xl mx-auto text-center">
                  <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/20 mx-auto mb-6">
                    <Icon name="Rocket" size={40} color="var(--color-primary)" />
                  </div>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground mb-4">
                    Ready to Get Started?
                  </h2>
                  <p className="text-base md:text-lg text-muted-foreground mb-8">
                    Whether you have a detailed project brief or just an idea, I'm here to help you navigate the development process from concept to launch.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a
                      href="mailto:contact@mikedev.pro"
                      className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold bg-primary text-primary-foreground hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 text-sm md:text-base w-full sm:w-auto"
                    >
                      <Icon name="Mail" size={20} />
                      <span>Send Email</span>
                    </a>
                    <a
                      href="https://calendly.com/mikedev"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold bg-card text-foreground border border-border hover:border-primary hover:shadow-lg transition-all duration-300 text-sm md:text-base w-full sm:w-auto"
                    >
                      <Icon name="Calendar" size={20} />
                      <span>Schedule Call</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16">
                <div className="p-6 rounded-lg bg-card border border-border text-center">
                  <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10 mx-auto mb-4">
                    <Icon name="Clock" size={28} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold font-heading text-foreground mb-2">
                    Fast Response
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Get a reply within 24 hours on business days
                  </p>
                </div>

                <div className="p-6 rounded-lg bg-card border border-border text-center">
                  <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10 mx-auto mb-4">
                    <Icon name="Shield" size={28} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold font-heading text-foreground mb-2">
                    Confidential
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Your project details are always protected
                  </p>
                </div>

                <div className="p-6 rounded-lg bg-card border border-border text-center">
                  <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10 mx-auto mb-4">
                    <Icon name="MessageSquare" size={28} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold font-heading text-foreground mb-2">
                    No Pressure
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Free consultation with no obligation to proceed
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Contact;